import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Container,
  IconButton,
  Box,
  useMediaQuery,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {BrandLogo, CloseModalIcon} from '@salesforce/retail-react-app/app/components/icons';
import {useBlurOverlay} from '@amblique/king-living/app/contexts/blur-overlay-context';
import Search from '@amblique/king-living/app/components/search';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';

/**
 * @override
 * - Add initialFocusRef to auto-focus search input when modal opens
 * - Use global blur overlay instead of ModalOverlay
 * - Pass inputRef to Search component for focus management
 * - On desktop: hide nav row while open; modal top/height follow collapsed header via ResizeObserver
 */
const SearchModal = ({isOpen, onClose}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('SearchModal');
  const searchInputRef = useRef(null);
  const {setSearchOpen} = useHeaderContext();

  // Notify header context about search modal state for header styling
  useEffect(() => {
    setSearchOpen(isOpen);
  }, [isOpen, setSearchOpen]);
  const headerWrapperRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const rafIdRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const [isMobile] = useMediaQuery('(max-width: 991px)');
  const [headerHeight, setHeaderHeight] = useState(0);
  const blurOverlay = useBlurOverlay();
  const wasSearchOpenRef = useRef(false);

  // Throttled header height calculation using requestAnimationFrame
  const calculateHeaderHeight = useCallback(() => {
    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      const headerWrapper = headerWrapperRef.current || document.querySelector('[data-header-wrapper]');

      if (headerWrapper) {
        // Cache the element reference
        if (!headerWrapperRef.current) {
          headerWrapperRef.current = headerWrapper;
        }

        const height = headerWrapper.getBoundingClientRect().height;

        // Only update state if height actually changed to avoid unnecessary re-renders
        setHeaderHeight((prevHeight) => {
          if (Math.abs(prevHeight - height) > 0.5) {
            return height;
          }

          return prevHeight;
        });
      }
    });
  }, []);

  // Calculate header height dynamically on desktop
  useEffect(() => {
    if (isMobile || !isOpen) {
      // Reset height when modal closes or on mobile
      setHeaderHeight(0);

      return;
    }

    // Initial calculation — after nav row is hidden (same commit as isSearchBoxOpen on Header).
    calculateHeaderHeight();
    requestAnimationFrame(() => {
      requestAnimationFrame(calculateHeaderHeight);
    });

    // Setup ResizeObserver with throttled callback
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        calculateHeaderHeight();
      });
    }

    const headerWrapper = document.querySelector('[data-header-wrapper]');

    if (headerWrapper) {
      headerWrapperRef.current = headerWrapper;
      resizeObserverRef.current.observe(headerWrapper);
    }

    // Throttled window resize listener (ResizeObserver handles most cases, this is fallback)
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(calculateHeaderHeight, 100);
    };

    window.addEventListener('resize', handleResize, {passive: true});

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [isMobile, isOpen, calculateHeaderHeight]);

  // Desktop: blur the page below the collapsed header (no ModalOverlay).
  // Mobile: SearchModal is a full-screen takeover — do not show the global blur overlay
  // (z-index 1590 sits above this modal and would hide the search UI).
  useEffect(() => {
    if (isOpen) {
      if (isMobile) {
        blurOverlay.hide();
      } else {
        blurOverlay.show({
          onForceClose: onClose,
        });
      }
      wasSearchOpenRef.current = true;
    } else if (wasSearchOpenRef.current) {
      blurOverlay.hide();
      wasSearchOpenRef.current = false;
    }
  }, [isOpen, isMobile, blurOverlay, onClose]);

  // Extract blockScrollOnMount from modal styles
  const {blockScrollOnMount, ...modalProps} = styles.modal || {};

  // Dynamic styles for content (not memoized)
  // Desktop: below collapsed header (nav hidden); top/height from ResizeObserver on [data-header-wrapper].
  const contentStyles = isMobile ? styles.content : {
    ...styles.content,
    display: 'flex',
    flexDirection: 'column',
    height: headerHeight > 0 ? `calc(100vh - ${headerHeight}px)` : 'auto',
    maxHeight: headerHeight > 0 ? `calc(100vh - ${headerHeight}px)` : 'none',
    maxW: '100%',
    overflowX: 'hidden',
    overflowY: 'hidden',
    width: '100%',
  };

  const contentContainerProps = isMobile ? styles.contentContainer : {
    ...styles.contentContainer,
    style: {
      ...styles.contentContainer?.style,
      zIndex: 1600,
      alignItems: 'flex-start',
      maxWidth: '100%',
      overflowX: 'hidden',
      ...(headerHeight > 0 && {
        height: `calc(100vh - ${headerHeight}px)`,
        top: `${headerHeight}px`,
      }),
    },
  };

  return (
    <Modal
      blockScrollOnMount={blockScrollOnMount ?? false}
      closeOnEsc
      data-testid="search-modal"
      initialFocusRef={searchInputRef}
      isOpen={isOpen}
      returnFocusOnClose
      scrollBehavior="inside"
      size="full"
      {...modalProps}
      onClose={onClose}
    >
      <ModalContent
        className="search-modal-content"
        containerProps={contentContainerProps}
        {...contentStyles}
      >
        <Box className="search-modal-content-inner" {...styles.contentInner}>
          {isMobile ? (
            <ModalHeader {...styles.mobileHeader}>
              <IconButton
                icon={<BrandLogo {...styles.logoMobile} />}
                variant="unstyled"
                onClick={onClose}
                {...styles.logoMobileWrapper}
              />
              <IconButton
                aria-label={intl.formatMessage({
                  defaultMessage: 'Close search',
                  id: 'search_modal.button.close',
                })}
                icon={<CloseModalIcon />}
                variant="unstyled"
                onClick={onClose}
                {...styles.closeButton}
              />
            </ModalHeader>
          ) : (
            <IconButton
              aria-label={intl.formatMessage({
                defaultMessage: 'Close search',
                id: 'search_modal.button.close',
              })}
              icon={<CloseModalIcon />}
              variant="ghost"
              onClick={onClose}
              {...styles.closeButton}
            />
          )}
          <ModalBody {...styles.body}>
            <Container {...styles.container}>
              <Search
                inputRef={searchInputRef}
                onCloseModal={onClose}
              />
            </Container>
          </ModalBody>
        </Box>
      </ModalContent>
    </Modal>
  );
};

SearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SearchModal;
